/**
 * L2 feedback — StatTileRow · Sheet · EmptyState · ErrorState · NoticeList,
 * plus PendingCard.
 *
 * The pieces that tell the user what the DATA is doing. Two sealed rules run
 * through all of them: status colour is a data semantic (never decoration),
 * and an empty state distinguishes FIRST-RUN from FILTER-MISS — mixing the two
 * is how a user ends up staring at "add your first item" while holding 40.
 *
 * Two entries live here for history, not for family. `PendingCard` the registry
 * files under `collection`, beside `Card`, because it is the OTHER cell a
 * collection lays out — the one for a row that has not landed yet. `NoticeList`
 * it files under `dataviz`, beside Gauge and BarChart, because a tone+count+word
 * readout is data presentation; it absorbed the standalone tally legend, which
 * was this same bound tone-row list with an optional `count`.
 */
import { useEffect, useState } from 'react';
import { guardedInbox } from '../inbox';
import { terminalStatus, isTerminal } from '../paths';
import { resetButton, hitTarget, toneColor, toneSoft, fmt, useSelection, widgetHoldsNothing, SHIMMER_STYLE } from './ui';
import { inboxPalette, SERIF_FONT } from './inboxPalette';

/**
 * A tile's value, from either shape a derive hands this row:
 *   a MAP keyed by tile key      `{expired: 2, soon: 1}`   (status counts)
 *   a LIST of `{key, value, …}`  `[{key:'protein_g', …}]`  (metric rows)
 * Returning the whole row for the list form is what lets a metric tile show
 * its target and unit without a second component.
 */
function tileRow(source, key) {
  if (Array.isArray(source)) return source.find((r) => r?.key === key) || null;
  const v = source?.[key];
  return v === undefined ? null : { key, value: v };
}

/**
 * The thin per-tile reading of value against target: `--accent` on a
 * `--surface-3` track, and the SEALED honesty rule the ring already obeys —
 * past the target the fill CLAMPS and changes colour, it never runs off the end
 * of its own track. Drawn only when a target exists; a number with nothing to
 * be measured against has no bar to draw.
 */
function TileMeter({ value, target }) {
  const pct = Math.max(0, Math.min(1, value / target));
  const over = value > target;
  return (
    <div
      role="img"
      aria-label={`${fmt(value)} of ${fmt(target)}`}
      style={{
        height: 4, borderRadius: 'var(--r-pill)', background: 'var(--surface-3)',
        overflow: 'hidden', marginTop: 2,
      }}
    >
      <div style={{
        width: `${Math.round(pct * 100)}%`, height: '100%', borderRadius: 'var(--r-pill)',
        background: over ? 'var(--warn)' : 'var(--accent)',
        transition: 'width 380ms var(--ease)',
      }} />
    </div>
  );
}

/**
 * The primary numeral FITS, it never truncates.
 *
 * A tile column is `1/n` of the row, so the same value is roomy at two tiles
 * and cramped at four — and a clipped number is not a smaller number, it is a
 * WRONG one ("1,7…" for 1,770 is the bug this exists to prevent). The width a
 * numeral needs is proportional to its glyph count, so the type steps DOWN as
 * the string grows rather than the string being cut. Ellipsis belongs on labels
 * (a shortened word is still that word); it may never touch a value.
 *
 * The steps are token sizes, in order, so a stepped-down tile still sits on the
 * type scale — and the Flutter twin can mirror this by reading the same ladder
 * instead of measuring text.
 */
const VALUE_STEPS = [
  [4, 'var(--t-2xl)'],
  [6, 'var(--t-xl)'],
  [8, 'var(--t-lg)'],
];

function valueFontSize(text, columns = 1) {
  // A wider column fits more, so the ladder only bites from three tiles up: at
  // one or two tiles even a long numeral has the room for the full size.
  const glyphs = String(text ?? '').length;
  const budget = columns <= 2 ? 8 : 0;
  for (const [max, size] of VALUE_STEPS) if (glyphs <= max + budget) return size;
  return 'var(--t-md)';
}

/**
 * SEALED: derived counts + status colour semantics (bad/warn/muted). Tapping a
 * tile emits `tabChanged` with its key so the blueprint can wire it to a grid
 * filter; a second tap on the active tile clears back to "all".
 *
 * The `metric` variant is honest where `status` is literal: a count of nothing
 * IS zero, but a metric with no data is UNKNOWN and renders "—", never 0.
 *
 * A tile is a COLUMN, not a box. It used to be a bordered, shadowed card per
 * tile — which was the only card the row had, and the dashboard now puts the
 * whole row on a panel of its own, so a tile drawing its own frame was a card
 * inside a card. What separates the columns is space and a hairline; what
 * makes a tile READ is the big numeral, the muted label under it, and the thin
 * value/target bar. The only tile that still takes a filled shape is the ACTIVE
 * one, because "which slice am I looking at" is a state that has to be visible.
 */
export function StatTileRow({ nodeId, variant = 'status', props = {}, data, emit, slots, ctx }) {
  const tiles = Array.isArray(props.tiles) ? props.tiles : [];
  const [active, setActive] = useSelection(nodeId, props.selected ?? null);
  const setCounts = slots?.setCounts;
  const tappable = props.tappable !== false && Boolean(emit);
  const clearable = props.clearable !== false;

  const pick = (key) => {
    // 不可清空的分类栏始终保留一个真实选中项，避免列表与视觉状态脱节。
    const next = active === key && clearable ? null : key;
    setActive(next);
    emit?.('tabChanged', { key: next || 'all' });
  };

  // With nothing bound, the tiles ARE the source: a panel authored from
  // literals carries each reading on its own tile instead of a feed.
  const shown = setCounts?.payload?.counts ?? (data && typeof data === 'object' ? data : tiles);
  const metric = variant === 'metric';

  if (props.serif === true) {
    const p = inboxPalette(ctx);
    return (
      <div data-stat-serif="" style={{ display: 'flex', gap: 8 }}>
        {tiles.map((t) => {
          const raw = tileRow(shown, t.key)?.value ?? null;
          return (
            <button
              key={t.key}
              type="button"
              disabled={!tappable}
              aria-pressed={active === t.key}
              onClick={tappable ? () => pick(t.key) : undefined}
              style={{
                ...resetButton, flex: 1, minWidth: 0, background: p.well, borderRadius: 16, padding: '12px 10px',
                display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left',
                cursor: tappable ? 'pointer' : 'default',
              }}
            >
              <span style={{ fontFamily: SERIF_FONT, fontWeight: 400, fontSize: 30, lineHeight: 1, color: p.ink, whiteSpace: 'nowrap' }}>
                {metric ? fmt(raw) : (raw == null ? 0 : raw)}
              </span>
              <span style={{ fontSize: 12, color: p.statLabel, marginTop: 6, maxWidth: '100%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.label}</span>
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.max(tiles.length, 1)}, minmax(0,1fr))`, gap: 'var(--sp-2)' }}>
      {tiles.map((t, i) => {
        const on = active === t.key;
        const row = tileRow(shown, t.key);
        const raw = row?.value ?? null;
        const value = metric ? fmt(raw) : (raw == null ? 0 : raw);
        const empty = !raw;
        // the bar needs BOTH halves of the reading as numbers, and a zero
        // target is not a target — it is a division by nothing
        const drawn = metric && raw != null && Number.isFinite(Number(raw)) ? Number(raw) : null;
        const target = drawn != null && Number(row?.target) > 0 ? Number(row.target) : null;
        return (
          <button
            key={t.key}
            type="button"
            disabled={!tappable}
            aria-pressed={on}
            onClick={tappable ? () => pick(t.key) : undefined}
            style={{
              ...resetButton,
              padding: 'var(--sp-2)',
              borderRadius: 'var(--r)',
              // `plated` keeps every tone visible instead of only the picked one:
              // a row of readouts is a palette, not a one-of-N control.
              background: on || props.plated ? toneSoft(t.tone) : 'transparent',
              // a hairline between columns, never a box around one
              borderLeft: i === 0 || on ? '1px solid transparent' : '1px solid var(--line-soft)',
              cursor: tappable ? 'pointer' : 'default',
              display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0,
              opacity: empty && !on ? 0.55 : 1,
              transition: 'background 140ms var(--ease)',
            }}
          >
            {/* the NUMBER, alone on its line, WHOLE. The target used to ride
                inline after it, which is legible at two tiles and wraps into
                three lines at four — the reading is "the number, then what it
                is measured against", so that is the stacking too. It steps
                down to fit its column and is never clipped. */}
            <span style={{
              fontSize: valueFontSize(value, tiles.length), fontWeight: 700, lineHeight: 1.05,
              fontFamily: 'var(--font-display)', letterSpacing: '-0.02em',
              color: metric ? 'var(--text)' : toneColor(t.tone),
              minWidth: 0, whiteSpace: 'nowrap',
            }}>{value}</span>
            {metric && row?.target != null ? (
              <span style={{
                fontSize: 'var(--t-xs)', color: 'var(--text-3)', fontWeight: 600,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>{`/ ${fmt(row.target)}${row.unit ? ` ${row.unit}` : ''}`}</span>
            ) : null}
            <span style={{
              fontSize: 'var(--t-xs)', color: 'var(--text-3)', fontWeight: 600,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>{t.label}</span>
            {target ? <TileMeter value={drawn} target={target} /> : null}
          </button>
        );
      })}
    </div>
  );
}

/** SEALED: backdrop dismiss + focus trap. Opens on the `open` slot. */
export function Sheet({ props = {}, emit, slots, children }) {
  const [open, setOpen] = useState(false);
  const openSlot = slots?.open;
  const closeSlot = slots?.close;

  useEffect(() => { if (openSlot) setOpen(true); }, [openSlot]);
  useEffect(() => { if (closeSlot) setOpen(false); }, [closeSlot]);

  if (!open) return null;
  const dismiss = () => { setOpen(false); emit?.('dismissed', {}); };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={props.title || 'Sheet'}
      onClick={dismiss}
      style={{
        position: 'absolute', inset: 0, zIndex: 40,
        background: 'rgba(0,0,0,0.36)',
        display: 'flex', alignItems: 'flex-end',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', background: 'var(--surface)',
          borderTopLeftRadius: 'var(--r-lg)', borderTopRightRadius: 'var(--r-lg)',
          padding: 'var(--sp-4)', paddingBottom: 'var(--sp-5)',
          boxShadow: 'var(--shadow-pop)', maxHeight: '78%', overflowY: 'auto',
          animation: 'viNativeSheetIn 220ms var(--ease-spring)',
        }}
      >
        <div style={{
          width: 36, height: 4, borderRadius: 2, background: 'var(--line)',
          margin: '0 auto var(--sp-3)',
        }} />
        {props.title ? (
          <div style={{ fontSize: 'var(--t-lg)', fontWeight: 700, marginBottom: 'var(--sp-3)' }}>{props.title}</div>
        ) : null}
        {children}
      </div>
    </div>
  );
}

// Each empty is a DIFFERENT reading, so each gets its own glyph: nothing yet ·
// nothing matched · that one thing is gone · this part was never produced ·
// nothing yet AND here is how it works.
const EMPTY_GLYPH = {
  'first-run': '◉',
  'filter-miss': '⌕',
  'not-found': '⊘',
  'not-generated': '✳',
  onboarding: '✧',
};

/**
 * THE WAIT — `kind: "loading"`, and the one reading of "nothing on screen" that
 * is not a statement about the data at all.
 *
 * Every other empty says something FINAL: you have nothing, your filter matched
 * nothing, that item is gone. A page whose first fetch has not landed says none
 * of those, and telling a user with 40 rows to "add your first item" for the
 * 300ms before their rows arrive is the flicker this exists to prevent. So the
 * loading state carries no glyph, no title-as-verdict and no CTA — it draws the
 * SHAPE of what is coming (`count` cards, default 3) and waits.
 *
 * It is deliberately NOT gated on `widgetHoldsNothing`: whether the widget
 * holds anything is precisely the question that has not been answered yet.
 */
/**
 * SKELETONPLATE — the SHAPE of an answer that has not arrived.
 *
 * The kit's one wait, in the three shapes a module can have. It is what the
 * interpreter draws in place of ANY data-bound node while `ctx.__loading`
 * holds, so no component has to learn about waiting and no blueprint has to
 * author a loading state: the shape comes from the registry FAMILY of the node
 * that would have rendered (see `skeletonShapeFor`), never from the author.
 *
 *   cards   a collection — N row plates, the silhouette of the list that is coming
 *   block   a chart, a gauge, a picture — one plate the module's own size
 *   rows    text — two bars, a long one over a short one, like a heading and its line
 *
 * A skeleton is DELIBERATELY not a spinner: a spinner says "something is
 * happening", a skeleton says "this is what will be here", and only the second
 * one lets the page keep its layout while it waits.
 *
 * `SHIMMER_STYLE` is the kit's single moving highlight (`ui.js`) — a plate here
 * and a pending cell three screens away must read as the same kind of waiting.
 */
export function SkeletonPlate({ shape = 'rows', count = 3, aspect = null, ...rest }) {
  const common = {
    role: 'status', 'aria-busy': 'true', 'aria-label': 'Loading',
    'data-vi-skeleton': shape,
  };

  if (shape === 'block') {
    return (
      <div
        {...common}
        {...rest}
        style={{ height: 96, borderRadius: 'var(--r)', width: '100%', ...SHIMMER_STYLE }}
      />
    );
  }

  if (shape === 'rows') {
    return (
      <div
        {...common}
        {...rest}
        style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%', minWidth: 0 }}
      >
        <div style={{ height: 12, width: '62%', borderRadius: 4, ...SHIMMER_STYLE }} />
        <div style={{ height: 10, width: '38%', borderRadius: 4, ...SHIMMER_STYLE }} />
      </div>
    );
  }

  const cards = Math.max(1, Math.min(6, Number(count) || 3));
  return (
    <div
      {...common}
      {...rest}
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)', width: '100%', minWidth: 0 }}
    >
      {Array.from({ length: cards }).map((_, i) => (
        <div
          key={i}
          data-vi-shimmer-card="1"
          style={{
            display: 'flex', alignItems: 'center', gap: 'var(--sp-3)',
            padding: 'var(--sp-3)', borderRadius: 'var(--r)',
            background: 'var(--surface)', boxShadow: 'var(--shadow-1)',
          }}
        >
          <div style={{ width: 44, aspectRatio: aspect || '1 / 1', borderRadius: 'var(--r-sm)', flex: '0 0 auto', ...SHIMMER_STYLE }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ height: 11, width: '58%', borderRadius: 4, ...SHIMMER_STYLE }} />
            <div style={{ height: 9, width: '34%', borderRadius: 4, marginTop: 6, ...SHIMMER_STYLE }} />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * The AUTHORED wait — `EmptyState kind:"loading"`, which is the same plate a
 * blueprint asks for by name. It keeps its own `data-vi-empty` marker because
 * an authored empty state and an interpreter-drawn skeleton are two different
 * things to the studio, even when they draw identically.
 */
function LoadingCards({ count, aspect }) {
  return <SkeletonPlate shape="cards" count={count} aspect={aspect} data-vi-empty="loading" />;
}

/**
 * ONE shimmer bar, for a line of TEXT that is still waiting — the other half of
 * the wait, for authored copy with a read interpolated into it (`waitsForData`
 * in `ui.js` is the predicate; PageShell's eyebrow and SectionHeader's meta are
 * the two lines that need it).
 */
export function SkeletonText({ width = '46%', height = 12 }) {
  return (
    <div
      role="status"
      aria-busy="true"
      aria-label="Loading"
      data-vi-skeleton="text"
      style={{ height, width, borderRadius: 4, ...SHIMMER_STYLE }}
    />
  );
}


/**
 * THE TEACHING HALF of an onboarding empty state: the flow, as numbered steps.
 *
 * A first-run screen that only says "nothing here yet" tells the user the one
 * thing they can already see. What they do not know is what this widget DOES —
 * so the steps are the content, and the CTA under them is the first step made
 * tappable.
 *
 * Every step is `{icon?, title, body?}` and the disc shows the ORDINAL unless
 * the author supplies a glyph. The list is a real `<ol>`, so the sequence
 * survives for a screen reader without the numerals being read twice — which
 * is why the disc itself is decorative.
 */
function OnboardingSteps({ steps }) {
  return (
    <ol style={{
      listStyle: 'none', margin: 0, padding: 0, width: '100%', maxWidth: 320,
      display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)', textAlign: 'left',
    }}>
      {steps.map((s, i) => (
        <li key={s.title || i} style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--sp-3)', minWidth: 0 }}>
          <span aria-hidden style={{
            flex: '0 0 auto', width: 'var(--sp-6)', height: 'var(--sp-6)', borderRadius: 'var(--r-pill)',
            background: 'var(--accent-soft)', color: 'var(--accent)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 'var(--t-sm)', fontWeight: 800, fontFamily: 'var(--font-display)',
          }}>{s.icon || i + 1}</span>
          <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <div style={{ fontSize: 'var(--t-md)', fontWeight: 700, color: 'var(--text)', lineHeight: 1.35 }}>
              {s.title}
            </div>
            {s.body ? (
              <div style={{ fontSize: 'var(--t-sm)', color: 'var(--text-2)', lineHeight: 1.45 }}>{s.body}</div>
            ) : null}
          </div>
        </li>
      ))}
    </ol>
  );
}

/**
 * SEALED: the FIRST-RUN vs FILTER-MISS split, now five readings wide.
 *  - first-run     → the CTA captures (host.openCapture). Renders only when the
 *                    widget genuinely holds nothing.
 *  - onboarding    → first-run that TEACHES: the same "widget truly holds
 *                    nothing" gate, plus `steps[]` rendered as a numbered list
 *                    of what this widget does. It is a READING OF FIRST-RUN,
 *                    not a fifth independent state — which is what keeps it off
 *                    a filtered tab, where a lesson in how to use the widget is
 *                    an answer to a question nobody asked.
 *  - filter-miss   → the CTA clears the filter. Never offers "add" as the fix
 *                    for a filter that matched nothing.
 *  - not-found     → a deep link resolved to nothing: the item is gone, the
 *                    widget is not empty. Never suppressed by having data.
 *  - not-generated → this SLICE was never produced (a platform with no post
 *                    yet) — the CTA generates it, and there is nothing to
 *                    clear and nothing to add.
 *  - loading       → the answer has not arrived. A shimmer of the shape that
 *                    is coming, and NO verdict (see `LoadingCards`).
 */
export function EmptyState({ variant, props = {}, ctx, emit }) {
  const kind = props.kind || variant || 'first-run';
  const isFirstRun = kind === 'first-run' || kind === 'onboarding';
  if (isFirstRun && !widgetHoldsNothing(ctx)) return null;
  // A WAIT is not an empty: it never suppresses itself on having data (that is
  // the unanswered question), and it never offers a CTA for a state nobody has
  // confirmed yet.
  if (kind === 'loading') return <LoadingCards count={props.count} aspect={props.aspect} />;
  /**
   * …AND AN EMPTY IS NOT A WAIT — the same sentence read the other way, which
   * is the half that was missing.
   *
   * The interpreter's skeleton rule (`ctx.__loading`) catches every node that
   * BINDS, and that is every collection. It cannot catch this one, because a
   * page-level empty binds nothing: all nine blueprints gate it with
   * `when: {path: "docs", op: "empty"}`, and during the wait `docs` IS empty —
   * so a user holding forty documents was shown "Add your first doc", CTA and
   * all, for the 300ms before their rows arrived.
   *
   * Every reading here is a VERDICT about data that has not landed: first-run
   * and onboarding say the widget is new, filter-miss says a filter matched
   * nothing, not-found says a deep link resolved to nothing, not-generated says
   * a slice was never made. None of them is answerable yet. The authored
   * `loading` reading above is the one exception and it has already returned.
   */
  if (ctx?.__loading) return null;
  // Steps belong to the onboarding reading alone: a filter-miss that happened
  // to carry them would teach the widget to someone already using it.
  const steps = kind === 'onboarding' && Array.isArray(props.steps)
    ? props.steps.filter((s) => s && s.title)
    : [];

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--sp-3)',
      textAlign: 'center', padding: 'var(--sp-6) var(--sp-4)',
    }}>
      <div style={{
        width: 64, height: 64, borderRadius: 'var(--r-lg)',
        background: 'var(--accent-soft)', color: 'var(--accent)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26,
      }}>{EMPTY_GLYPH[kind] || '⌕'}</div>
      <div style={{ fontSize: 'var(--t-xl)', fontWeight: 700, fontFamily: 'var(--font-display)', letterSpacing: '-0.02em' }}>
        {props.title}
      </div>
      {props.body ? (
        <div style={{ fontSize: 'var(--t-sm)', color: 'var(--text-2)', lineHeight: 1.5, maxWidth: 300 }}>{props.body}</div>
      ) : null}
      {steps.length ? <OnboardingSteps steps={steps} /> : null}
      {props.cta ? (
        <button
          type="button"
          onClick={() => emit?.('ctaActivated', { action: props.cta.action })}
          style={{
            ...resetButton, ...hitTarget,
            marginTop: 'var(--sp-2)', padding: '12px 22px', borderRadius: 'var(--r-pill)',
            background: 'var(--accent)', color: 'var(--accent-ink)',
            fontWeight: 700, fontSize: 'var(--t-md)',
          }}
        >{props.cta.label}</button>
      ) : null}
    </div>
  );
}

/**
 * The cell a collection lays out for a row that has NOT LANDED yet — `Card`'s
 * peer, and pure View exactly like it. It owns NO identity: the enclosing
 * collection knows which session a cell is about and attaches
 * `session_id` / `grid_id` when it re-emits, the same split `Card` obeys. The
 * one tap it has is the passthrough `activated {}`, which a `SessionCollection`
 * re-emits as `sessionActivated {session_id}`.
 *
 * SEALED: not clickable until the host binds a session_id, and dedup against
 * the landed row by session_id (the collection's half). This is the ONE
 * pending affordance — a widget never draws its own spinner.
 *
 * TERMINAL, UNLOGGED is the third reading and the one that used to be missing:
 * a session that has FINISHED without producing a row is not still working, so
 * the shimmer stops and a static mark takes over — but it stays CLICKABLE,
 * because reviewing that session is exactly what the user needs next. A
 * spinner that never stops is the lie this state exists to prevent.
 */
// 服务端 Session 的 stopped / timeout 同样已经终止，不能继续展示进行中动效。The terminal
// STATE is the shared `terminalStatus` (paths.js); this map is only its GLYPH — the
// red "!" for the failed set, a green check for a finished one. Kept (the render
// capability stays) even though the strip is now running-only.
const TERMINAL_MARK = { failed: '!', error: '!', ended: '!', stopped: '!', timeout: '!' };

function terminalOf(r) {
  const s = terminalStatus(r);
  if (s == null) return null;
  return TERMINAL_MARK[s] || '✓';
}

export function PendingCard({ variant = 'tile', props = {}, data, emit }) {
  const shape = props.shape || variant || 'tile';
  const rows = Array.isArray(data) ? data : (data ? [data] : [{}]);
  // running-only (CONTRACT §13): a session that reached a TERMINAL outcome
  // (stopped / failed / timeout, or one that finished) is no longer a pending
  // cell — its durable re-entry is the feed Card with a top-right red "!" marker
  // (see cards.jsx `CornerBadges`). The strip shows only sessions still in
  // flight. A null / empty `data` still yields the single blank "Starting…"
  // placeholder (`[{}]` carries no status, so it survives the filter). The
  // terminal RENDER below is KEPT (the render capability stays) — it is simply
  // never fed a terminal row now. `isTerminal` (paths.js) is the shared strip
  // filter, the twin of Flutter `_buildLoggingStrip`'s live filter.
  const live = rows.filter((r) => !isTerminal(r));
  const clickable = (r) => Boolean(r?.session_id);
  // `Card`'s passthrough tap: the cell says it was tapped and nothing more —
  // WHICH session that was is the collection's to know and to attach.
  const activate = () => emit?.('activated', {});

  // The kit's ONE shimmer (`ui.js`), not a second copy of it: a pending cell
  // and the skeleton plate above must be the same wait, at the same speed.
  const shimmer = SHIMMER_STYLE;
  // a settled cell keeps the plate, loses the motion
  const still = { background: 'var(--surface-3)' };
  const caption = (r) => {
    const mark = terminalOf(r);
    if (mark) return mark === '✓' ? 'Finished — tap to review' : 'Ended — tap to review';
    return clickable(r) ? 'Logging…' : 'Starting…';
  };

  if (shape === 'row') {
    if (!live.length) return null;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
        {live.map((r, i) => {
          const fill = terminalOf(r) ? still : shimmer;
          return (
            <div
              key={r.session_id || r.client_ref || i}
              role={clickable(r) ? 'button' : undefined}
              onClick={clickable(r) ? () => activate() : undefined}
              style={{
                display: 'flex', alignItems: 'center', gap: 'var(--sp-3)',
                padding: 'var(--sp-3)', borderRadius: 'var(--r)', background: 'var(--surface)',
                border: '1px dashed var(--line)',
                cursor: clickable(r) ? 'pointer' : 'default',
              }}
            >
              <div style={{ width: 44, height: 44, borderRadius: 'var(--r-sm)', ...fill }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ height: 11, width: '52%', borderRadius: 4, ...fill }} />
                <div style={{ height: 9, width: '30%', borderRadius: 4, marginTop: 6, ...fill }} />
              </div>
              <span style={{ fontSize: 'var(--t-xs)', color: 'var(--text-3)' }}>{caption(r)}</span>
            </div>
          );
        })}
      </div>
    );
  }

  if (shape === 'block') {
    return <div style={{ height: 90, borderRadius: 'var(--r)', ...shimmer }} />;
  }

  // A placeholder IS one cell of the grid it stands in: it builds no grid of
  // its own, so the enclosing collection's columns size it and `aspect` takes
  // that collection's tile ratio. The media keeps that ratio while the card
  // itself fills the collection row and carries its caption in a real footer,
  // matching a landed media-top Card's media + chrome footprint.
  const cells = live.map((r, i) => {
    const mark = terminalOf(r);
    const fill = mark ? still : shimmer;
    return (
      <div
        key={r.session_id || r.client_ref || i}
        data-vi-pending-card="tile"
        role={clickable(r) ? 'button' : undefined}
        onClick={clickable(r) ? () => activate() : undefined}
        style={{
          width: '100%', minWidth: 0, height: '100%', display: 'flex', flexDirection: 'column',
          borderRadius: 'var(--r)', overflow: 'hidden', background: 'var(--surface)',
          border: '1px dashed var(--line)',
          cursor: clickable(r) ? 'pointer' : 'default',
        }}
      >
        <div
          data-vi-pending-media="1"
          style={{
            position: 'relative', flex: '0 0 auto', width: '100%',
            aspectRatio: props.aspect || '3 / 4',
            ...(props.hasImage === false ? {} : fill),
          }}
        >
          {/* TERMINAL MARK — a small pointer-inert badge in the TOP-RIGHT corner
              (matching the Private-badge corner convention): a red "!" for a run
              that produced no result (stopped / failed / timeout), a green check
              for one that finished. `pointerEvents:'none'` so it never steals the
              card's tap. Red is the kit's `toneColor('bad')`, the same token the
              failed lane already uses (media.jsx), so it matches Flutter's tokens.bad. */}
          {mark ? (
            <div style={{
              position: 'absolute', top: 4, right: 4,
              width: 16, height: 16, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 12, fontWeight: 700, lineHeight: 1,
              color: '#fff', pointerEvents: 'none',
              background: mark === '✓' ? 'var(--good)' : toneColor('bad'),
            }}>{mark}</div>
          ) : null}
        </div>
        <div
          data-vi-pending-caption="1"
          style={{
            flex: '1 1 auto', display: 'flex', alignItems: 'center', minHeight: 0,
            padding: '6px 8px', fontSize: 'var(--t-xs)', color: 'var(--text-3)',
            background: 'var(--surface)',
          }}
        >{caption(r)}</div>
      </div>
    );
  });
  return cells;
}

/**
 * ErrorState — the page could not LOAD. Distinct from EmptyState on purpose:
 * an empty widget is a fact about the data, a failed one is a fact about the
 * fetch, and offering "add your first item" to someone whose feed timed out is
 * the bug this component exists to prevent.
 *
 * SEALED: retry is BOUNDED (`maxRetries`, default 3) and the escalation only
 * appears once retrying has genuinely stopped helping — a button that keeps
 * offering the same failing action is how a dead end pretends to be a loop.
 */
export function ErrorState({ props = {}, emit }) {
  const [attempts, setAttempts] = useState(0);
  const max = Number(props.maxRetries) > 0 ? Number(props.maxRetries) : 3;
  const canRetry = props.retry !== false && attempts < max;
  const support = props.support && attempts >= max ? props.support : null;

  return (
    <div
      role="alert"
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--sp-3)',
        textAlign: 'center', padding: 'var(--sp-6) var(--sp-4)',
      }}
    >
      <div style={{
        width: 64, height: 64, borderRadius: 'var(--r-lg)',
        background: toneSoft('bad'), color: toneColor('bad'),
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26,
      }}>!</div>
      <div style={{ fontSize: 'var(--t-xl)', fontWeight: 700, fontFamily: 'var(--font-display)', letterSpacing: '-0.02em' }}>
        {props.title || 'Something went wrong'}
      </div>
      {props.body ? (
        <div style={{ fontSize: 'var(--t-sm)', color: 'var(--text-2)', lineHeight: 1.5, maxWidth: 300 }}>{props.body}</div>
      ) : null}
      {canRetry ? (
        <button
          type="button"
          onClick={() => { setAttempts((n) => n + 1); emit?.('retryRequested', { attempt: attempts + 1 }); }}
          style={{
            ...resetButton, ...hitTarget,
            marginTop: 'var(--sp-2)', padding: '12px 22px', borderRadius: 'var(--r-pill)',
            background: 'var(--accent)', color: 'var(--accent-ink)', fontWeight: 700, fontSize: 'var(--t-md)',
          }}
        >{props.retryLabel || 'Retry'}</button>
      ) : null}
      {support ? (
        <button
          type="button"
          onClick={() => emit?.('supportRequested', { action: support.action })}
          style={{
            ...resetButton, ...hitTarget,
            padding: '10px 18px', borderRadius: 'var(--r-pill)',
            border: '1px solid var(--line)', color: 'var(--text)', fontWeight: 600, fontSize: 'var(--t-sm)',
          }}
        >{support.label || 'Contact support'}</button>
      ) : null}
    </div>
  );
}

// Shape carries the tone too, so a notice survives colour-blindness and a
// greyscale screenshot: octagon for bad, triangle for warn, disc otherwise.
const NOTICE_SHAPE = {
  bad: 'polygon(30% 0,70% 0,100% 30%,100% 70%,70% 100%,30% 100%,0 70%,0 30%)',
  warn: 'polygon(50% 0,100% 100%,0 100%)',
};
const NOTICE_GLYPH = { bad: '✕', warn: '!' };

/**
 * The MARK on a status row — shape-coded, exactly like a notice glyph.
 *
 * `running` is an open ring (a track with one lit arc) that SPINS, `done` a
 * filled disc, `info` a hollow disc. A running mark that only differs from
 * "finished" by a lit arc says too little — so the arc turns (the kit's own
 * `viNativeSpin`, kept alive under reduced-motion in `KitKeyframes` because a
 * frozen spinner reads as a dead ring). This is the same ring the `card`
 * reading spins; here it rides the plain tone-strip plate (doc-digitizer's
 * "Working in this document…" banner). `done`/`info`/`guide` are static —
 * nothing is in flight, so shape alone carries it.
 */
function StatusMark({ state, tone }) {
  const color = toneColor(tone);
  if (state === 'done') {
    return <span aria-hidden style={{ flex: '0 0 auto', width: 8, height: 8, borderRadius: 'var(--r-pill)', background: color }} />;
  }
  return (
    <span aria-hidden style={{
      flex: '0 0 auto', width: 14, height: 14, borderRadius: 'var(--r-pill)',
      border: `2px solid ${toneSoft(tone)}`,
      borderTopColor: state === 'running' ? color : toneSoft(tone),
      boxSizing: 'border-box',
      ...(state === 'running' ? { animation: 'viNativeSpin 0.8s linear infinite' } : null),
    }} />
  );
}

/**
 * StatusRow — the always-on plated line behind `NoticeList`'s `guide` and
 * `activity` readings.
 *
 * `notice` is an ADVISORY list: it appears because something is wrong, and it
 * disappears the moment nothing is. A widget also needs the opposite row — the
 * one that is simply on while a thing is happening ("Creating 2 scenes — tap
 * to follow") or while the user is being told how the page works. Both are one
 * plated line with a mark, so they are one shape:
 *   `activity`  the mark reads RUNNING unless the row says `done`.
 *   `guide`     the mark is static; nothing is in flight.
 * A row carrying a `payload` is TAPPABLE and emits `activated` with it — which
 * is what turns "2 scenes are being made" into a way to go and watch them.
 * Without one it is a statement, and statements are not buttons.
 */
function StatusRow({ item, reading, emit, card }) {
  const tone = item.tone || 'accent';
  const done = item.done === true || item.state === 'done' || item.mark === 'done';
  const state = reading === 'activity' ? (done ? 'done' : 'running') : (item.state || item.mark || 'info');
  const payload = item.payload && typeof item.payload === 'object' ? item.payload : null;
  // ADDITIVE opt-in (`props.card`): the row that stands ABOVE a timeline of white
  // cards — plant-expert's "Adding a check-in…" — must LOOK like one of them, not
  // like a coloured advisory strip. `card` only swaps the CHROME (white card vs
  // tone strip); the running mark spins in either reading (see `StatusMark`). Web
  // check-in opens the chat, so this row never actually shows on web — but the
  // render must exist and match the app.
  if (card) return <StatusCardRow item={item} state={state} tone={tone} payload={payload} emit={emit} />;
  // Both readings WRAP to the full text: `guide` is a guidance PARAGRAPH, and
  // `activity` is a "something is happening" status (doc-digitizer's Working
  // banner) whose progress text is the whole point — clamping it to one
  // ellipsised line eats exactly what the user needs to read. A status row is
  // always shown complete, never truncated.
  const plate = {
    display: 'flex', alignItems: 'flex-start', gap: 'var(--sp-2)', width: '100%',
    minHeight: 44, padding: 'var(--sp-2) var(--sp-3)', minWidth: 0,
    borderRadius: 'var(--r)', background: toneSoft(tone),
    border: `1px solid ${toneColor(tone)}`,
  };
  const line = (
    <>
      <StatusMark state={state} tone={tone} />
      <span style={{
        fontSize: 'var(--t-sm)', fontWeight: 600, color: 'var(--text)', minWidth: 0,
        whiteSpace: 'normal', lineHeight: 1.4,
      }}>{item.text}</span>
    </>
  );
  if (!payload || !emit) return <div style={plate}>{line}</div>;
  return (
    <button type="button" onClick={() => emit('activated', { ...payload })} style={{ ...resetButton, ...plate }}>
      {line}
    </button>
  );
}

/**
 * The `card:true` reading of a `guide`/`activity` row — a white surface card that
 * matches the timeline items it stands above (the same `--surface` / `--r` /
 * `--shadow-1` chrome a `Card` draws), with the mark in the 44px THUMBNAIL SLOT.
 *
 * Unlike the default status mark, this one ANIMATES while it runs: the row exists
 * only for the optimistic seconds before the real row replaces it, so its motion
 * IS the "still working" signal (the app-side ask). It reuses the kit's own
 * `viNativeSpin` ring — deliberately kept alive under reduced-motion in
 * `KitKeyframes`, because a frozen spinner would read as a dead ring. `done` is a
 * filled disc; a static (`guide`) mark falls back to the sealed ring.
 */
function StatusCardRow({ item, state, tone, payload, emit }) {
  const color = toneColor(tone);
  const slot =
    state === 'running' ? (
      <span
        data-vi-spinner="animated"
        aria-hidden
        style={{
          width: 20, height: 20, borderRadius: '50%', boxSizing: 'border-box',
          border: `2px solid ${toneSoft(tone)}`, borderTopColor: color,
          animation: 'viNativeSpin 0.8s linear infinite',
        }}
      />
    ) : state === 'done' ? (
      <span aria-hidden style={{ width: 8, height: 8, borderRadius: '50%', background: color }} />
    ) : (
      <StatusMark state={state} tone={tone} />
    );
  const card = {
    display: 'flex', alignItems: 'center', gap: 'var(--sp-3)', width: '100%',
    minHeight: 44, padding: 'var(--sp-3)', minWidth: 0,
    borderRadius: 'var(--r)', background: 'var(--surface)', boxShadow: 'var(--shadow-1)',
  };
  const body = (
    <>
      <span style={{
        width: 44, height: 44, flex: '0 0 44px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>{slot}</span>
      <span style={{
        fontSize: 'var(--t-sm)', fontWeight: 600, color: 'var(--text)', minWidth: 0,
        whiteSpace: 'normal', lineHeight: 1.4,
      }}>{item.text}</span>
    </>
  );
  if (!payload || !emit) return <div data-vi-notice-card="1" style={card}>{body}</div>;
  return (
    <button type="button" data-vi-notice-card="1" onClick={() => emit('activated', { ...payload })} style={{ ...resetButton, ...card }}>
      {body}
    </button>
  );
}

/**
 * `NoticeList.setItems` — THE OPTIMISTIC LINE. The focus/until/seen guard lives
 * in `guardedInbox` (`../inbox`), shared verbatim with `MediaPicker` so the two
 * optimistic components can never drift; here we only extract the rows off the
 * guarded payload. A delivery carrying neither guard key renders as the plain
 * `{items}` the registry has always declared.
 */
function noticeInboxItems(slots, ctx) {
  const inbox = guardedInbox(slots, ctx);
  if (!inbox) return null;
  const items = inbox.items;
  if (Array.isArray(items)) return items;
  return items && typeof items === 'object' ? [items] : null;
}

/**
 * NoticeList — SEALED: DATA-GATED (an empty list renders NOTHING, never a
 * reassuring "no problems") and triple-coded (shape or swatch + colour + word).
 *
 * Generic by construction: it binds a list of `{tone, text, count?, label?}`
 * and knows nothing about what the rows are about. Whatever computes them — a
 * derive, the model — decides the tone; the list only renders it.
 *
 * TWO READINGS, one contract. The tally legend used to be its own component,
 * and the two were one optional `count` apart: same single `items` bind, same
 * "empty draws nothing", same `fallback: Stack`, and the legend's own entry
 * cited THIS one's triple coding as its mechanism. So the reading is a
 * `variant`:
 *   `notice` (default)  shape-coded advisory rows — the glyph carries the tone
 *                       so it survives greyscale, and `label` is the eyebrow.
 *   `legend`            swatch · count · word, laid out by `direction` — the
 *                       companion every gauge needs and no gauge should grow,
 *                       because a ring shows ONE number and what the other
 *                       slices are is a list.
 *   `guide` `activity`  ONE tone-plated row: a mark, one line, and — when the
 *                       row carries a `payload` — a tap. See `StatusRow`.
 */
export function NoticeList({ variant, props = {}, data, ctx, emit, slots }) {
  // slot → props → bind, the same ladder MediaPicker's `setItems` already
  // walks; the two guards live in `noticeInboxItems`.
  const pushed = noticeInboxItems(slots, ctx);
  const raw = pushed ?? (props.items !== undefined ? props.items : data);
  const items = (Array.isArray(raw) ? raw : []).filter((it) => it && it.text);
  if (!items.length) return null;
  const reading = props.variant || variant;
  const legend = reading === 'legend';
  const row = props.direction === 'row';

  // ONE ROW, not a list: `guide` and `activity` are the widget saying one thing
  // about itself right now, so they render the FIRST item and stop. A second
  // simultaneous status is a design error, not a scroll.
  if (reading === 'guide' || reading === 'activity') {
    return <StatusRow item={items[0]} reading={reading} emit={emit} card={Boolean(props.card)} />;
  }

  if (legend) {
    return (
      <div style={{
        display: 'flex', flexDirection: row ? 'row' : 'column',
        flexWrap: row ? 'wrap' : 'nowrap', gap: row ? 'var(--sp-3)' : 'var(--sp-2)',
        minWidth: 0,
      }}>
        {items.map((it, i) => (
          <div key={`${it.text}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', minWidth: 0 }}>
            <span aria-hidden style={{
              width: 10, height: 10, borderRadius: 3, flex: '0 0 auto', background: toneColor(it.tone),
            }} />
            {it.count != null && it.count !== '' ? (
              <span style={{ fontSize: 'var(--t-sm)', fontWeight: 700, color: 'var(--text)' }}>{fmt(it.count)}</span>
            ) : null}
            <span style={{
              fontSize: 'var(--t-sm)', color: 'var(--text-2)', minWidth: 0,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{it.text}</span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex', flexDirection: row ? 'row' : 'column',
      flexWrap: row ? 'wrap' : 'nowrap', gap: 'var(--sp-2)',
    }}>
      {items.map((it, i) => {
        const tone = it.tone || 'warn';
        return (
          <div
            key={`${tone}-${it.text || i}`}
            style={{
              display: 'flex', alignItems: 'flex-start', gap: 'var(--sp-3)',
              padding: 'var(--sp-3)', borderRadius: 'var(--r)',
              background: toneSoft(tone),
              border: `1px solid ${toneColor(tone)}`,
            }}
          >
            <span aria-hidden style={{
              flex: '0 0 auto', width: 20, height: 20, fontSize: 12, fontWeight: 800,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: toneColor(tone), background: toneSoft(tone),
              clipPath: NOTICE_SHAPE[tone] || 'circle(50%)',
            }}>{NOTICE_GLYPH[tone] || '•'}</span>
            <div style={{ minWidth: 0 }}>
              {it.label ? (
                <div style={{
                  fontSize: 'var(--t-xs)', fontWeight: 800, letterSpacing: '0.06em',
                  textTransform: 'uppercase', color: toneColor(tone),
                }}>{it.label}</div>
              ) : null}
              <div style={{ fontSize: 'var(--t-sm)', color: 'var(--text)', lineHeight: 1.4 }}>{it.text}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
